/**
 * Anchor week: D0 = Sunday 2026-03-22 (UTC).
 * Covered range: Saturday 2026-03-21 (D-1) through Sunday 2026-03-29 (D7).
 *
 * Use the dedicated personal timeline loader to shift these timestamps so
 * D0 aligns with the user's current-week Sunday.
 *
 * Extras duration config follows the BE defaults referenced in the Java config:
 * - signOn: 35m, signOff: 15m, garaging default: 30m
 * - fuelling: 15m, cleaning: 45m (per master prompt defaults)
 */

const STATION_TAT = {
	code: 'TAT6',
	id: 'station_tat6',
	timezone: 'America/Chicago',
	name: 'Tatooine Dune Port',
	city: { id: 'city_tatooine', name: 'Tatooine' },
};
const STATION_NAB = {
	code: 'NAB6',
	id: 'station_nab6',
	timezone: 'America/Chicago',
	name: 'Naboo Lake Hub',
	city: { id: 'city_naboo', name: 'Naboo' },
};
const STATION_HOT = {
	code: 'HOT6',
	id: 'station_hot6',
	timezone: 'America/Chicago',
	name: 'Hoth Ice Depot',
	city: { id: 'city_hoth', name: 'Hoth' },
};
const STATION_COR = {
	code: 'COR6',
	id: 'station_cor6',
	timezone: 'America/Chicago',
	name: 'Coruscant Central',
	city: { id: 'city_coruscant', name: 'Coruscant' },
};
const STATION_END = {
	code: 'END6',
	id: 'station_end6',
	timezone: 'America/Chicago',
	name: 'Endor Forest Post',
	city: { id: 'city_endor', name: 'Endor' },
};
const STATION_JAK = {
	code: 'JAK6',
	id: 'station_jak6',
	timezone: 'America/Chicago',
	name: 'Jakku Sand Terminal',
	city: { id: 'city_jakku', name: 'Jakku' },
};
const STATION_MUS = {
	code: 'MUS6',
	id: 'station_mus6',
	timezone: 'America/Chicago',
	name: 'Mustafar Lava Yard',
	city: { id: 'city_mustafar', name: 'Mustafar' },
};

export const RAW_PERSONAL_TIMELINE_WEEK = {
	activitiesV2: [
		// ============================================================
		// D-1 (Saturday, pre-week) — shift starts 18:00, ends 09:00 Sun (D0)
		// Tests left clipping: Saturday portion outside week view.
		// ============================================================
		{
			fromTimestamp: '2026-03-21T18:00:00Z',
			toTimestamp: '2026-03-22T09:00:00Z',
			activity: {
				__typename: 'CrewMemberWorkAssignmentActivityItem',
				title: 'MUS6 → TAT6',
				status: 'actual',
				activityType: 'driving',
				rideId: 'ride_mus6_tat6_210326_1',
				sequenceGroupId: 'seq_group_preweek_210326',
				shiftId: 'shift_preweek_210326',
				fromStation: STATION_MUS,
				toStation: STATION_TAT,
				note: '',
				extraActivities: [
					{
						id: 'extra_signon_mus6_210326',
						shiftId: 'shift_preweek_210326',
						fromTimestamp: '2026-03-21T17:25:00Z',
						toTimestamp: '2026-03-21T18:00:00Z',
						activityType: 'signOn',
						fromStation: { code: 'MUS6' },
						sequenceGroupId: 'seq_group_preweek_210326',
					},
					{
						id: 'extra_signoff_tat6_220326',
						shiftId: 'shift_preweek_210326',
						fromTimestamp: '2026-03-22T09:00:00Z',
						toTimestamp: '2026-03-22T09:15:00Z',
						activityType: 'signOff',
						fromStation: { code: 'TAT6' },
						sequenceGroupId: 'seq_group_preweek_210326',
					},
				],
			},
			isCancellable: false,
		},

		// ============================================================
		// D0 (Sun) — rest 09:15..17:00, then shift 17:00..23:00
		// ============================================================
		{
			fromTimestamp: '2026-03-22T09:15:00Z',
			toTimestamp: '2026-03-22T17:00:00Z',
			activity: { __typename: 'CrewMemberBreakActivityItem', breakType: 'rest' },
			isCancellable: false,
		},
		{
			fromTimestamp: '2026-03-22T17:35:00Z',
			toTimestamp: '2026-03-22T22:45:00Z',
			activity: {
				__typename: 'CrewMemberWorkAssignmentActivityItem',
				title: 'TAT6 → NAB6',
				status: 'confirmed',
				activityType: 'driving',
				rideId: 'ride_tat6_nab6_220326_1',
				sequenceGroupId: 'seq_group_tat6_nab6_220326',
				shiftId: 'shift_sun_220326',
				fromStation: STATION_TAT,
				toStation: STATION_NAB,
				note: '',
				extraActivities: [
					{
						id: 'extra_signon_tat6_220326',
						shiftId: 'shift_sun_220326',
						fromTimestamp: '2026-03-22T17:00:00Z',
						toTimestamp: '2026-03-22T17:35:00Z',
						activityType: 'signOn',
						fromStation: { code: 'TAT6' },
						sequenceGroupId: 'seq_group_tat6_nab6_220326',
					},
					{
						id: 'extra_signoff_nab6_220326',
						shiftId: 'shift_sun_220326',
						fromTimestamp: '2026-03-22T22:45:00Z',
						toTimestamp: '2026-03-22T23:00:00Z',
						activityType: 'signOff',
						fromStation: { code: 'NAB6' },
						sequenceGroupId: 'seq_group_tat6_nab6_220326',
					},
				],
			},
			isCancellable: false,
		},

		// ============================================================
		// D0 Sun 23:00 → D1 Mon 06:00 — overnight rest between Sun shift and Mon shift
		// ============================================================
		{
			fromTimestamp: '2026-03-22T23:00:00Z',
			toTimestamp: '2026-03-23T06:00:00Z',
			activity: { __typename: 'CrewMemberBreakActivityItem', breakType: 'rest' },
			isCancellable: false,
		},

		// ============================================================
		// D1 (Mon) — shift 06:00..20:00, multi-ride, covers all extra types
		// ============================================================
		{
			fromTimestamp: '2026-03-23T07:00:00Z',
			toTimestamp: '2026-03-23T12:00:00Z',
			activity: {
				__typename: 'CrewMemberWorkAssignmentActivityItem',
				title: 'NAB6 → HOT6',
				status: 'actual',
				activityType: 'driving',
				rideId: 'ride_nab6_hot6_230326_1',
				sequenceGroupId: 'seq_group_nab6_hot6_230326',
				shiftId: 'shift_mon_230326',
				fromStation: STATION_NAB,
				toStation: STATION_HOT,
				note: '',
				extraActivities: [
					{
						id: 'extra_signon_nab6_230326',
						shiftId: 'shift_mon_230326',
						fromTimestamp: '2026-03-23T06:00:00Z',
						toTimestamp: '2026-03-23T06:35:00Z',
						activityType: 'signOn',
						fromStation: { code: 'NAB6' },
						sequenceGroupId: 'seq_group_nab6_hot6_230326',
					},
					{
						id: 'extra_cleaning_nab6_230326',
						shiftId: 'shift_mon_230326',
						fromTimestamp: '2026-03-23T06:35:00Z',
						toTimestamp: '2026-03-23T07:00:00Z',
						activityType: 'cleaning',
						fromStation: { code: 'NAB6' },
						sequenceGroupId: 'seq_group_nab6_hot6_230326',
					},
					{
						id: 'extra_fuelling_hot6_230326',
						shiftId: 'shift_mon_230326',
						fromTimestamp: '2026-03-23T12:00:00Z',
						toTimestamp: '2026-03-23T12:15:00Z',
						activityType: 'fuelling',
						fromStation: { code: 'HOT6' },
						sequenceGroupId: 'seq_group_nab6_hot6_230326',
					},
				],
			},
			isCancellable: false,
		},
		{
			fromTimestamp: '2026-03-23T12:15:00Z',
			toTimestamp: '2026-03-23T19:30:00Z',
			activity: {
				__typename: 'CrewMemberWorkAssignmentActivityItem',
				title: 'HOT6 → COR6',
				status: 'pending',
				activityType: 'driving',
				rideId: 'ride_hot6_cor6_230326_1',
				sequenceGroupId: 'seq_group_hot6_cor6_230326',
				shiftId: 'shift_mon_230326',
				fromStation: STATION_HOT,
				toStation: STATION_COR,
				note: '',
				extraActivities: [
					{
						id: 'extra_garaging_cor6_230326',
						shiftId: 'shift_mon_230326',
						fromTimestamp: '2026-03-23T19:30:00Z',
						toTimestamp: '2026-03-23T20:00:00Z',
						activityType: 'garaging',
						fromStation: { code: 'COR6' },
						sequenceGroupId: 'seq_group_hot6_cor6_230326',
					},
					{
						id: 'extra_signoff_cor6_230326',
						shiftId: 'shift_mon_230326',
						fromTimestamp: '2026-03-23T20:00:00Z',
						toTimestamp: '2026-03-23T20:15:00Z',
						activityType: 'signOff',
						fromStation: { code: 'COR6' },
						sequenceGroupId: 'seq_group_hot6_cor6_230326',
					},
				],
			},
			isCancellable: false,
		},

		// ============================================================
		// D1 → D2 rest overnight (short — makes room for D2 non-driving shift)
		// ============================================================
		{
			fromTimestamp: '2026-03-23T20:15:00Z',
			toTimestamp: '2026-03-24T04:00:00Z',
			activity: { __typename: 'CrewMemberBreakActivityItem', breakType: 'rest' },
			isCancellable: false,
		},

		// ============================================================
		// D2 (Tue) 04:00 → 12:00 — NON-DRIVING shift (protection + traveling + deadhead)
		// Tests rendering of shifts whose parent activities are all non-driving types.
		// ============================================================
		{
			fromTimestamp: '2026-03-24T04:35:00Z',
			toTimestamp: '2026-03-24T07:00:00Z',
			activity: {
				__typename: 'CrewMemberWorkAssignmentActivityItem',
				title: 'Protection COR6',
				status: 'confirmed',
				activityType: 'protection',
				rideId: null,
				sequenceGroupId: 'seq_group_tue_nondriving_240326',
				shiftId: 'shift_tue_nondriving_240326',
				fromStation: STATION_COR,
				toStation: STATION_COR,
				note: '',
				extraActivities: [
					{
						id: 'extra_signon_cor6_240326_am',
						shiftId: 'shift_tue_nondriving_240326',
						fromTimestamp: '2026-03-24T04:00:00Z',
						toTimestamp: '2026-03-24T04:35:00Z',
						activityType: 'signOn',
						fromStation: { code: 'COR6' },
						sequenceGroupId: 'seq_group_tue_nondriving_240326',
					},
				],
			},
			isCancellable: false,
		},
		{
			fromTimestamp: '2026-03-24T07:00:00Z',
			toTimestamp: '2026-03-24T09:30:00Z',
			activity: {
				__typename: 'CrewMemberWorkAssignmentActivityItem',
				title: 'COR6 → END6',
				status: 'confirmed',
				activityType: 'traveling',
				rideId: null,
				sequenceGroupId: 'seq_group_tue_nondriving_240326',
				shiftId: 'shift_tue_nondriving_240326',
				fromStation: STATION_COR,
				toStation: STATION_END,
				note: '',
				extraActivities: [],
			},
			isCancellable: false,
		},
		{
			fromTimestamp: '2026-03-24T09:30:00Z',
			toTimestamp: '2026-03-24T11:45:00Z',
			activity: {
				__typename: 'CrewMemberWorkAssignmentActivityItem',
				title: 'END6 → COR6',
				status: 'confirmed',
				activityType: 'deadhead',
				rideId: null,
				sequenceGroupId: 'seq_group_tue_nondriving_240326',
				shiftId: 'shift_tue_nondriving_240326',
				fromStation: STATION_END,
				toStation: STATION_COR,
				note: '',
				extraActivities: [
					{
						id: 'extra_signoff_cor6_240326_am',
						shiftId: 'shift_tue_nondriving_240326',
						fromTimestamp: '2026-03-24T11:45:00Z',
						toTimestamp: '2026-03-24T12:00:00Z',
						activityType: 'signOff',
						fromStation: { code: 'COR6' },
						sequenceGroupId: 'seq_group_tue_nondriving_240326',
					},
				],
			},
			isCancellable: false,
		},

		// ============================================================
		// D2 mid-day rest between non-driving shift and overnight shift
		// ============================================================
		{
			fromTimestamp: '2026-03-24T12:00:00Z',
			toTimestamp: '2026-03-24T22:00:00Z',
			activity: { __typename: 'CrewMemberBreakActivityItem', breakType: 'rest' },
			isCancellable: false,
		},

		// ============================================================
		// D2 (Tue) 22:00 → D3 (Wed) 06:00 — multi-day shift INSIDE the week
		// Tests midnight chunking within the week view.
		// ============================================================
		{
			fromTimestamp: '2026-03-24T22:35:00Z',
			toTimestamp: '2026-03-25T05:45:00Z',
			activity: {
				__typename: 'CrewMemberWorkAssignmentActivityItem',
				title: 'COR6 → END6',
				status: 'confirmed',
				activityType: 'driving',
				rideId: 'ride_cor6_end6_240326_1',
				sequenceGroupId: 'seq_group_cor6_end6_240326',
				shiftId: 'shift_tue_overnight_240326',
				fromStation: STATION_COR,
				toStation: STATION_END,
				note: '',
				extraActivities: [
					{
						id: 'extra_signon_cor6_240326',
						shiftId: 'shift_tue_overnight_240326',
						fromTimestamp: '2026-03-24T22:00:00Z',
						toTimestamp: '2026-03-24T22:35:00Z',
						activityType: 'signOn',
						fromStation: { code: 'COR6' },
						sequenceGroupId: 'seq_group_cor6_end6_240326',
					},
					{
						id: 'extra_signoff_end6_250326',
						shiftId: 'shift_tue_overnight_240326',
						fromTimestamp: '2026-03-25T05:45:00Z',
						toTimestamp: '2026-03-25T06:00:00Z',
						activityType: 'signOff',
						fromStation: { code: 'END6' },
						sequenceGroupId: 'seq_group_cor6_end6_240326',
					},
				],
			},
			isCancellable: false,
		},

		// ============================================================
		// D3 (Wed) rest 06:00..14:00, then shift 14:00..23:00 WITH autoClose
		// ============================================================
		{
			fromTimestamp: '2026-03-25T06:00:00Z',
			toTimestamp: '2026-03-25T14:00:00Z',
			activity: { __typename: 'CrewMemberBreakActivityItem', breakType: 'rest' },
			isCancellable: false,
		},
		{
			fromTimestamp: '2026-03-25T14:35:00Z',
			toTimestamp: '2026-03-25T18:35:00Z',
			activity: {
				__typename: 'CrewMemberWorkAssignmentActivityItem',
				title: 'END6 → JAK6',
				status: 'actual',
				activityType: 'driving',
				rideId: 'ride_end6_jak6_250326_1',
				sequenceGroupId: 'seq_group_end6_jak6_250326',
				shiftId: 'shift_wed_autoclose_250326',
				fromStation: STATION_END,
				toStation: STATION_JAK,
				note: '',
				extraActivities: [
					{
						id: 'extra_signon_end6_250326',
						shiftId: 'shift_wed_autoclose_250326',
						fromTimestamp: '2026-03-25T14:00:00Z',
						toTimestamp: '2026-03-25T14:35:00Z',
						activityType: 'signOn',
						fromStation: { code: 'END6' },
						sequenceGroupId: 'seq_group_end6_jak6_250326',
					},
					{
						id: 'extra_cleaning_jak6_250326',
						shiftId: 'shift_wed_autoclose_250326',
						fromTimestamp: '2026-03-25T18:35:00Z',
						toTimestamp: '2026-03-25T18:50:00Z',
						activityType: 'cleaning',
						fromStation: { code: 'JAK6' },
						sequenceGroupId: 'seq_group_end6_jak6_250326',
					},
				],
			},
			isCancellable: false,
		},

		// ============================================================
		// D3 → D4 rest
		// ============================================================
		{
			fromTimestamp: '2026-03-25T23:00:00Z',
			toTimestamp: '2026-03-26T05:00:00Z',
			activity: { __typename: 'CrewMemberBreakActivityItem', breakType: 'rest' },
			isCancellable: false,
		},

		// ============================================================
		// D4 (Thu) — single-day shift 05:00..18:00
		// ============================================================
		{
			fromTimestamp: '2026-03-26T05:35:00Z',
			toTimestamp: '2026-03-26T17:45:00Z',
			activity: {
				__typename: 'CrewMemberWorkAssignmentActivityItem',
				title: 'JAK6 → MUS6',
				status: 'actual',
				activityType: 'driving',
				rideId: 'ride_jak6_mus6_260326_1',
				sequenceGroupId: 'seq_group_jak6_mus6_260326',
				shiftId: 'shift_thu_260326',
				fromStation: STATION_JAK,
				toStation: STATION_MUS,
				note: '',
				extraActivities: [
					{
						id: 'extra_signon_jak6_260326',
						shiftId: 'shift_thu_260326',
						fromTimestamp: '2026-03-26T05:00:00Z',
						toTimestamp: '2026-03-26T05:35:00Z',
						activityType: 'signOn',
						fromStation: { code: 'JAK6' },
						sequenceGroupId: 'seq_group_jak6_mus6_260326',
					},
					{
						id: 'extra_fuelling_mus6_260326',
						shiftId: 'shift_thu_260326',
						fromTimestamp: '2026-03-26T17:45:00Z',
						toTimestamp: '2026-03-26T18:00:00Z',
						activityType: 'fuelling',
						fromStation: { code: 'MUS6' },
						sequenceGroupId: 'seq_group_jak6_mus6_260326',
					},
					{
						id: 'extra_signoff_mus6_260326',
						shiftId: 'shift_thu_260326',
						fromTimestamp: '2026-03-26T18:00:00Z',
						toTimestamp: '2026-03-26T18:15:00Z',
						activityType: 'signOff',
						fromStation: { code: 'MUS6' },
						sequenceGroupId: 'seq_group_jak6_mus6_260326',
					},
				],
			},
			isCancellable: false,
		},

		// ============================================================
		// D4 Thu 18:15 → D5 Fri 00:00 — rest between Thu shift end and Fri absence
		// ============================================================
		{
			fromTimestamp: '2026-03-26T18:15:00Z',
			toTimestamp: '2026-03-27T00:00:00Z',
			activity: { __typename: 'CrewMemberBreakActivityItem', breakType: 'rest' },
			isCancellable: false,
		},

		// ============================================================
		// D5 (Fri) — absence (day off)
		// ============================================================
		{
			fromTimestamp: '2026-03-27T00:00:00Z',
			toTimestamp: '2026-03-27T23:59:59Z',
			activity: {
				__typename: 'CrewMemberAbsenceActivityItem',
				note: '',
				reason: { id: 'reason_day_off', name: 'Day off' },
			},
			isCancellable: false,
		},

		// ============================================================
		// D5 Fri 23:59:59 → D6 Sat 14:00 — RRO time off, availability disabled
		// Spans the overnight gap after the Fri "Day off" absence through
		// Saturday early afternoon. Renders via RroTimeOffAvailabilityDisabledLayout.
		// ============================================================
		{
			fromTimestamp: '2026-03-28T00:00:00Z',
			toTimestamp: '2026-03-28T14:00:00Z',
			activity: {
				__typename: 'CrewMemberAbsenceActivityItem',
				note: '',
				availableForAssignments: false,
				reason: {
					id: 'reason_rro_time_off',
					name: 'Regular operator time off',
				},
			},
			isCancellable: false,
		},

		// ============================================================
		// Next-week 3-day absence (Wed 1 Apr → Fri 3 Apr) — appears when the
		// user navigates to the following week. Offset 3 days from the week
		// start so it doesn't interrupt the post-week shift tail. Exercises
		// multi-day absence chip rendering across three days.
		// ============================================================
		{
			fromTimestamp: '2026-04-01T00:00:00Z',
			toTimestamp: '2026-04-03T23:59:59Z',
			activity: {
				__typename: 'CrewMemberAbsenceActivityItem',
				note: '',
				reason: { id: 'reason_day_off', name: 'Day off' },
			},
			isCancellable: false,
		},

		// ============================================================
		// D6 Sat 14:00 → 19:00 — rest between RRO block and post-week shift
		// ============================================================
		{
			fromTimestamp: '2026-03-28T14:00:00Z',
			toTimestamp: '2026-03-28T19:00:00Z',
			activity: { __typename: 'CrewMemberBreakActivityItem', breakType: 'rest' },
			isCancellable: false,
		},

		// ============================================================
		// D6 (Sat) 19:00 → D7 (Sun, post-week) 10:00 — tests RIGHT clipping
		// ============================================================
		{
			fromTimestamp: '2026-03-28T19:35:00Z',
			toTimestamp: '2026-03-29T09:45:00Z',
			activity: {
				__typename: 'CrewMemberWorkAssignmentActivityItem',
				title: 'MUS6 → TAT6',
				status: 'pending',
				activityType: 'driving',
				rideId: 'ride_mus6_tat6_280326_1',
				sequenceGroupId: 'seq_group_postweek_280326',
				shiftId: 'shift_postweek_280326',
				fromStation: STATION_MUS,
				toStation: STATION_TAT,
				note: '',
				extraActivities: [
					{
						id: 'extra_signon_mus6_280326',
						shiftId: 'shift_postweek_280326',
						fromTimestamp: '2026-03-28T19:00:00Z',
						toTimestamp: '2026-03-28T19:35:00Z',
						activityType: 'signOn',
						fromStation: { code: 'MUS6' },
						sequenceGroupId: 'seq_group_postweek_280326',
					},
					{
						id: 'extra_signoff_tat6_290326',
						shiftId: 'shift_postweek_280326',
						fromTimestamp: '2026-03-29T09:45:00Z',
						toTimestamp: '2026-03-29T10:00:00Z',
						activityType: 'signOff',
						fromStation: { code: 'TAT6' },
						sequenceGroupId: 'seq_group_postweek_280326',
					},
				],
			},
			isCancellable: false,
		},
	],

	shifts: [
		// Pre-week shift (Saturday 18:00 → Sunday 09:15) — left clipping
		{
			id: 'shift_preweek_210326',
			fromTimestamp: '2026-03-21T17:25:00Z',
			toTimestamp: '2026-03-22T09:15:00Z',
			autoCloseTimestamp: null,
		},
		// Sunday evening shift
		{
			id: 'shift_sun_220326',
			fromTimestamp: '2026-03-22T17:00:00Z',
			toTimestamp: '2026-03-22T23:00:00Z',
			autoCloseTimestamp: null,
		},
		// Monday shift (fully packed)
		{
			id: 'shift_mon_230326',
			fromTimestamp: '2026-03-23T06:00:00Z',
			toTimestamp: '2026-03-23T20:15:00Z',
			autoCloseTimestamp: null,
		},
		// Tuesday early-morning non-driving shift (protection + traveling + deadhead)
		{
			id: 'shift_tue_nondriving_240326',
			fromTimestamp: '2026-03-24T04:00:00Z',
			toTimestamp: '2026-03-24T12:00:00Z',
			autoCloseTimestamp: null,
		},
		// Tue → Wed overnight shift (midnight crossing within week)
		{
			id: 'shift_tue_overnight_240326',
			fromTimestamp: '2026-03-24T22:00:00Z',
			toTimestamp: '2026-03-25T06:00:00Z',
			autoCloseTimestamp: null,
		},
		// Wed shift with autoClose marker
		{
			id: 'shift_wed_autoclose_250326',
			fromTimestamp: '2026-03-25T14:00:00Z',
			toTimestamp: '2026-03-25T23:00:00Z',
			autoCloseTimestamp: '2026-03-25T22:30:00Z',
		},
		// Thursday shift
		{
			id: 'shift_thu_260326',
			fromTimestamp: '2026-03-26T05:00:00Z',
			toTimestamp: '2026-03-26T18:15:00Z',
			autoCloseTimestamp: null,
		},
		// Post-week shift (Saturday 19:00 → Sunday 10:00) — right clipping
		{
			id: 'shift_postweek_280326',
			fromTimestamp: '2026-03-28T19:00:00Z',
			toTimestamp: '2026-03-29T10:00:00Z',
			autoCloseTimestamp: null,
		},
	],
};
